import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Bot, Briefcase, Loader2, Mail, Lock, User, Gift, Eye, EyeOff } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AuthPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login, register } = useAuth();
  
  const initialMode = searchParams.get('mode') || 'login';
  const initialType = searchParams.get('type') || 'client';
  const referralCodeFromUrl = searchParams.get('ref') || '';
  const redirectUrl = searchParams.get('redirect') || '/dashboard';
  
  const [mode, setMode] = useState(initialMode);
  const [loading, setLoading] = useState(false);
  const [showLoginPassword, setShowLoginPassword] = useState(false);
  const [showRegisterPassword, setShowRegisterPassword] = useState(false);
  
  // Login form
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  
  // Register form
  const [registerName, setRegisterName] = useState('');
  const [registerEmail, setRegisterEmail] = useState('');
  const [registerPassword, setRegisterPassword] = useState('');
  const [userType, setUserType] = useState(initialType);
  const [referralCode, setReferralCode] = useState(referralCodeFromUrl);
  const [referralInfo, setReferralInfo] = useState(null);

  // Validate referral code
  useEffect(() => {
    const validateReferral = async () => {
      if (referralCode && referralCode.length >= 6) {
        try {
          const response = await axios.get(`${API}/referral/validate/${referralCode}`);
          if (response.data.valid) {
            setReferralInfo(response.data);
          } else {
            setReferralInfo(null);
          }
        } catch (error) {
          setReferralInfo(null);
        }
      } else {
        setReferralInfo(null);
      }
    };
    validateReferral();
  }, [referralCode]);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!loginEmail || !loginPassword) {
      toast.error('Please fill in all fields');
      return;
    }
    
    setLoading(true);
    try {
      await login(loginEmail, loginPassword);
      toast.success('Welcome back!');
      navigate(redirectUrl);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (!registerName || !registerEmail || !registerPassword) {
      toast.error('Please fill in all fields');
      return;
    }
    
    setLoading(true);
    try {
      const result = await register(registerEmail, registerPassword, registerName, userType, referralCode || null);
      if (result.referralBonus) {
        toast.success(result.referralBonus);
      }
      toast.success('Account created successfully!');
      navigate(redirectUrl);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="pt-24 pb-16 min-h-screen flex items-center justify-center" data-testid="auth-page">
      <div className="max-w-md w-full mx-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-zinc-900/50 border border-white/10 rounded-2xl p-8"
        >
          {/* Logo */}
          <div className="text-center mb-8">
            <div className="w-12 h-12 rounded-xl bg-indigo-500 flex items-center justify-center mx-auto mb-4">
              <Bot className="w-7 h-7 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-white">
              {mode === 'login' ? 'Welcome Back' : 'Create Account'}
            </h1>
            <p className="text-zinc-500 text-sm mt-2">
              {mode === 'login' 
                ? 'Sign in to access your dashboard' 
                : 'Join the AI agent marketplace'}
            </p>
          </div>

          <Tabs value={mode} onValueChange={setMode} className="w-full">
            <TabsList className="grid w-full grid-cols-2 bg-zinc-800/50 mb-6">
              <TabsTrigger value="login" className="data-[state=active]:bg-indigo-500">
                Log In
              </TabsTrigger>
              <TabsTrigger value="register" className="data-[state=active]:bg-indigo-500">
                Sign Up
              </TabsTrigger>
            </TabsList>

            <TabsContent value="login">
              <form onSubmit={handleLogin} className="space-y-4" aria-label="Login form">
                <div>
                  <Label htmlFor="login-email" className="text-zinc-300 mb-2 block">Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" aria-hidden="true" />
                    <Input
                      id="login-email"
                      type="email"
                      placeholder="you@example.com"
                      value={loginEmail}
                      onChange={(e) => setLoginEmail(e.target.value)}
                      className="pl-10 bg-zinc-800 border-white/10"
                      data-testid="login-email"
                      aria-required="true"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="login-password" className="text-zinc-300 mb-2 block">Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" aria-hidden="true" />
                    <Input
                      id="login-password"
                      type={showLoginPassword ? "text" : "password"}
                      placeholder="••••••••"
                      value={loginPassword}
                      onChange={(e) => setLoginPassword(e.target.value)}
                      className="pl-10 pr-10 bg-zinc-800 border-white/10"
                      data-testid="login-password"
                      aria-required="true"
                    />
                    <button
                      type="button"
                      onClick={() => setShowLoginPassword(!showLoginPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300 transition-colors"
                      aria-label={showLoginPassword ? "Hide password" : "Show password"}
                    >
                      {showLoginPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-indigo-500 hover:bg-indigo-600 text-white h-12 rounded-none mt-6"
                  data-testid="login-submit"
                  aria-label={loading ? "Signing in..." : "Sign in to your account"}
                >
                  {loading ? (
                    <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
                  ) : (
                    'Sign In'
                  )}
                </Button>
                <div className="text-center mt-4">
                  <a 
                    href="/forgot-password" 
                    className="text-sm text-zinc-400 hover:text-teal-400 transition-colors"
                  >
                    Forgot your password?
                  </a>
                </div>
              </form>
            </TabsContent>

            <TabsContent value="register">
              <form onSubmit={handleRegister} className="space-y-4" aria-label="Registration form">
                {/* User Type Selection */}
                <fieldset className="mb-6">
                  <legend className="sr-only">Select account type</legend>
                  <div className="grid grid-cols-2 gap-4">
                  <button
                    type="button"
                    onClick={() => setUserType('client')}
                    className={`p-4 rounded-xl border transition-all ${
                      userType === 'client'
                        ? 'border-indigo-500 bg-indigo-500/10'
                        : 'border-white/10 hover:border-white/30'
                    }`}
                    data-testid="select-client"
                    aria-pressed={userType === 'client'}
                    aria-label="Select patron account type - I need AI agents"
                  >
                    <Briefcase className={`w-6 h-6 mx-auto mb-2 ${
                      userType === 'client' ? 'text-indigo-400' : 'text-zinc-500'
                    }`} aria-hidden="true" />
                    <p className={`text-sm font-medium ${
                      userType === 'client' ? 'text-white' : 'text-zinc-400'
                    }`}>Become a Patron</p>
                    <p className="text-xs text-zinc-500 mt-1">I need AI agents</p>
                  </button>
                  <button
                    type="button"
                    onClick={() => setUserType('agent_owner')}
                    className={`p-4 rounded-xl border transition-all ${
                      userType === 'agent_owner'
                        ? 'border-cyan-500 bg-cyan-500/10'
                        : 'border-white/10 hover:border-white/30'
                    }`}
                    data-testid="select-agent-owner"
                    aria-pressed={userType === 'agent_owner'}
                    aria-label="Select agent owner account type - I have AI agents"
                  >
                    <Bot className={`w-6 h-6 mx-auto mb-2 ${
                      userType === 'agent_owner' ? 'text-cyan-400' : 'text-zinc-500'
                    }`} aria-hidden="true" />
                    <p className={`text-sm font-medium ${
                      userType === 'agent_owner' ? 'text-white' : 'text-zinc-400'
                    }`}>List Agents</p>
                    <p className="text-xs text-zinc-500 mt-1">I have AI agents</p>
                  </button>
                  </div>
                </fieldset>

                <div>
                  <Label htmlFor="register-name" className="text-zinc-300 mb-2 block">Full Name</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" aria-hidden="true" />
                    <Input
                      id="register-name"
                      type="text"
                      placeholder="John Doe"
                      value={registerName}
                      onChange={(e) => setRegisterName(e.target.value)}
                      className="pl-10 bg-zinc-800 border-white/10"
                      data-testid="register-name"
                      aria-required="true"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="register-email" className="text-zinc-300 mb-2 block">Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" aria-hidden="true" />
                    <Input
                      id="register-email"
                      type="email"
                      placeholder="you@example.com"
                      value={registerEmail}
                      onChange={(e) => setRegisterEmail(e.target.value)}
                      className="pl-10 bg-zinc-800 border-white/10"
                      data-testid="register-email"
                      aria-required="true"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="register-password" className="text-zinc-300 mb-2 block">Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" aria-hidden="true" />
                    <Input
                      id="register-password"
                      type={showRegisterPassword ? "text" : "password"}
                      placeholder="••••••••"
                      value={registerPassword}
                      onChange={(e) => setRegisterPassword(e.target.value)}
                      className="pl-10 pr-10 bg-zinc-800 border-white/10"
                      data-testid="register-password"
                      aria-required="true"
                    />
                    <button
                      type="button"
                      onClick={() => setShowRegisterPassword(!showRegisterPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300 transition-colors"
                      aria-label={showRegisterPassword ? "Hide password" : "Show password"}
                    >
                      {showRegisterPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                
                {/* Referral Code */}
                <div>
                  <Label htmlFor="referral-code" className="text-zinc-300 mb-2 block">
                    Referral Code <span className="text-zinc-500">(optional)</span>
                  </Label>
                  <div className="relative">
                    <Gift className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" aria-hidden="true" />
                    <Input
                      id="referral-code"
                      type="text"
                      placeholder="Enter referral code"
                      value={referralCode}
                      onChange={(e) => setReferralCode(e.target.value.toUpperCase())}
                      className={`pl-10 bg-zinc-800 border-white/10 uppercase ${referralInfo?.valid ? 'border-green-500/50' : ''}`}
                      data-testid="referral-code"
                    />
                  </div>
                  {referralInfo?.valid && (
                    <div className="mt-2 p-3 bg-green-500/10 border border-green-500/30 rounded-lg">
                      <p className="text-green-400 text-sm">
                        Referred by <span className="font-semibold">{referralInfo.referrer_name}</span>
                      </p>
                      <p className="text-green-300 text-xs mt-1">{referralInfo.bonus}</p>
                    </div>
                  )}
                </div>
                
                <Button
                  type="submit"
                  disabled={loading}
                  className={`w-full h-12 rounded-none mt-6 ${
                    userType === 'agent_owner'
                      ? 'bg-cyan-500 hover:bg-cyan-600'
                      : 'bg-indigo-500 hover:bg-indigo-600'
                  } text-white`}
                  data-testid="register-submit"
                  aria-label={loading ? "Creating account..." : "Create your account"}
                >
                  {loading ? (
                    <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
                  ) : (
                    'Create Account'
                  )}
                </Button>
              </form>
            </TabsContent>
          </Tabs>
        </motion.div>
      </div>
    </div>
  );
}
